import type { RootState } from '@/store/rootReducer'
import { CanvasOverlayState } from '@/constants/app-options'
import type { SuggestionsButtonSet } from '@/constants/app-options'

export const selectAIFashionState = (state: RootState) => state.editor.aiFashionReducer

export const selectAIFashionSession = (state: RootState) => selectAIFashionState(state).session

export const selectAIFashionStatus = (state: RootState) => selectAIFashionState(state).status

export const selectAIFashionResults = (state: RootState) => selectAIFashionState(state).results

export const selectAIFashionHasGeneratedResults = (state: RootState): boolean => (
  selectAIFashionResults(state).some(result => result.kind === 'generated')
)

export const selectAIFashionHasDownloadableResults = (state: RootState): boolean => (
  selectAIFashionResults(state).some(result => result.kind === 'generated' || result.kind === 'video')
)

export const selectAIFashionSelectedResultId = (state: RootState) => selectAIFashionState(state).selectedResultId

export const selectAIFashionAccessDecision = (state: RootState) => selectAIFashionState(state).access

export const selectAIFashionPoseTiles = (state: RootState) => selectAIFashionState(state).poseTiles

export const isAIFashionEditorState = (
  editorState: {
    suggestionsButtonSet?: string | null
    navbarButtonSet?: string | null
  },
): boolean => (
  editorState.suggestionsButtonSet === 'aiFashion' ||
  (
    editorState.suggestionsButtonSet === 'ghost' &&
    editorState.navbarButtonSet === 'aiFashion'
  )
)

export const getAIFashionReadySuggestionsButtonSet = (
  suggestionsButtonSet: SuggestionsButtonSet,
): SuggestionsButtonSet => (
  suggestionsButtonSet === 'ghost' ? 'aiFashion' : suggestionsButtonSet
)

export const selectAIFashionIsLoading = (state: RootState): boolean => {
  const status = selectAIFashionStatus(state)
  if (status.kind === 'loading') return true

  const editorState = state.editor.editor.editorState
  if (editorState.navbarButtonSet !== 'aiFashion') return false

  // Phase B migration fallback: legacy editor adapter paths still drive
  // loader state through disableEditorState. The slice status is canonical
  // when present; this fallback is scoped to AI Fashion chrome to avoid
  // treating unrelated gradient overlays as AI Fashion loading.
  const disableState = state.editor.editor.disableEditorState
  return (
    disableState.canvasOverlay === CanvasOverlayState.gradientObjectNoText ||
    disableState.canvasOverlay === CanvasOverlayState.gradientObject
  )
}

export const selectIsAIFashionMode = (state: RootState): boolean => {
  const editorState = state.editor.editor.editorState
  return isAIFashionEditorState(editorState)
}

export const selectAIFashionDisableDownloadAll = (state: RootState): boolean => (
  selectAIFashionIsLoading(state) || !selectAIFashionHasDownloadableResults(state)
)
